/**
 * RESPONSIBILITY: decide what the hero shows next - one full photograph, or a group - and which photographs.
 *
 * RANDOM IN WHAT KIND OF SCENE COMES NEXT, CYCLIC IN WHICH PHOTOGRAPHS. Nobody can tell
 * whether a single or a group is coming, but each set goes through its own photographs in
 * order and starts over at the end - so every photograph gets its turn, none is skipped or
 * favoured, and an editor can see why a picture is where it is.
 *
 * TWO POOLS, NEVER MIXED. A full-width photograph is only ever shown full width, and a group
 * is only ever made of the photographs made for groups. Nothing is cropped into a frame it
 * was not made for.
 *
 * THE RANDOM PART HAS ONE RULE: the same size is allowed twice running but never a third
 * time. Strict alternation is itself a pattern, but three the same is the stuck look.
 * A size that cannot be drawn - a pair without two group photographs, a trio without three or
 * without the room - is never chosen, so a scene is never left empty.
 *
 * Pure and given its randomness, so it can be tested without a browser.
 */
export interface Scene {
  id: number;
  kind: 'single' | 'group';
  /** Indexes into that kind's pool, left to right. One, two or three. */
  picks: number[];
  /** Which side of a pair stands higher; swaps every scene. */
  flip: boolean;
  /** How many scenes in a row, this one included, have had this many photographs. */
  run: number;
  /** Where each pool carries on from. */
  fromSingles: number;
  fromGroups: number;
}

export interface Pools {
  /** Full-width photographs. */
  singles: number;
  /** Photographs made for groups; zero when the screen has no room for any. */
  groups: number;
}

type Rnd = () => number;

/** Weight of each size. A trio is the busiest, so the rarest. */
const WEIGHTS: [number, number][] = [[1, 0.4], [2, 0.4], [3, 0.2]];

const fits = (n: number, p: Pools, maxGroup: number) => (n === 1 ? p.singles > 0 : n <= maxGroup && n <= p.groups);

function sizeOf(p: Pools, maxGroup: number, prev: Scene | undefined, rnd: Rnd): number {
  let table = WEIGHTS.filter(([n]) => fits(n, p, maxGroup));
  if (table.length === 0) table = [[1, 1]];
  const capped = prev && prev.run >= 2 ? table.filter(([n]) => n !== prev.picks.length) : table;
  const pool = capped.length ? capped : table;
  let at = rnd() * pool.reduce((s, [, w]) => s + w, 0);
  for (const [n, w] of pool) { if ((at -= w) < 0) return n; }
  return pool[pool.length - 1][0];
}

const run = (from: number, size: number, count: number) => Array.from({ length: size }, (_, i) => (from + i) % count);

function build(id: number, size: number, p: Pools, from: [number, number], flip: boolean, runLen: number): Scene {
  // With no full photographs at all there is only the group pool to draw from.
  const kind = size === 1 && p.singles > 0 ? 'single' : 'group';
  const n = kind === 'single' ? 1 : Math.max(size, 2);
  const [fromS, fromG] = from;
  return {
    id, kind, flip, run: runLen,
    picks: kind === 'single' ? run(fromS, 1, p.singles) : run(fromG, n, p.groups),
    fromSingles: kind === 'single' ? (fromS + 1) % p.singles : fromS,
    fromGroups: kind === 'group' ? (fromG + n) % p.groups : fromG,
  };
}

/** The opening picture alone, so a visitor meets the one the editor put first. */
export function firstScene(p: Pools, rnd: Rnd = Math.random): Scene {
  return build(1, 1, p, [0, 0], rnd() < 0.5, 1);
}

export function nextScene(prev: Scene, p: Pools, maxGroup: number, rnd: Rnd = Math.random): Scene {
  const size = sizeOf(p, maxGroup, prev, rnd);
  return build(prev.id + 1, size, p, [prev.fromSingles, prev.fromGroups], !prev.flip, size === prev.picks.length ? prev.run + 1 : 1);
}
